import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Footer from "../../components/Footer";
import logoImage from "../../assets/LogoImage.png";
import theme from "../../theme";

// ─── Animations ──────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Page Shell ──────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  background: ${theme.colors.background.main};
  min-height: 100vh;
  color: ${theme.colors.text.primary};
  font-family: "Inter", sans-serif;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 60px;
  border-bottom: 1px solid ${theme.colors.border};
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${theme.colors.bgColor};

  @media (max-width: 768px) {
    padding: 16px 24px;
  }
`;

const Logo = styled.img`
  width: 180px;
  height: 34px;
  object-fit: contain;
  cursor: pointer;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 32px;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${theme.colors.primary};
  }
`;

const TryMeBtn = styled.button`
  background: transparent;
  border: 1.5px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  padding: 8px 22px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${theme.colors.primary};
    color: #0c0a1d;
  }
`;

// ─── Section Shared ───────────────────────────────────────────────────────────
const Section = styled.section`
  padding: 80px 60px;
  max-width: 1200px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease both;

  @media (max-width: 768px) {
    padding: 60px 24px;
  }
`;

const SectionLabel = styled.p`
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${theme.colors.primary};
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0 0 12px;
  line-height: 1.2;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1rem;
  color: ${theme.colors.text.tertiary};
  margin: 0 auto 48px;
  max-width: 520px;
`;

// ─── Billing Toggle ───────────────────────────────────────────────────────────
const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-bottom: 56px;
`;

const ToggleLabel = styled.span`
  font-size: 0.95rem;
  color: ${(p) =>
    p.active ? theme.colors.text.primary : theme.colors.text.tertiary};
  font-weight: ${(p) => (p.active ? 600 : 400)};
`;

const ToggleTrack = styled.button`
  width: 48px;
  height: 26px;
  border-radius: 13px;
  border: none;
  background: ${(p) => (p.annual ? theme.colors.primary : theme.colors.border)};
  cursor: pointer;
  position: relative;
  transition: background 0.25s;
  flex-shrink: 0;
`;

const ToggleThumb = styled.span`
  position: absolute;
  top: 3px;
  left: ${(p) => (p.annual ? "25px" : "3px")};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.25s;
`;

const SaveBadge = styled.span`
  background: rgba(64, 224, 208, 0.15);
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary};
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 0.75rem;
  font-weight: 600;
`;

// ─── Pricing Cards ────────────────────────────────────────────────────────────
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    max-width: 480px;
    margin: 0 auto;
  }
`;

const PlanCard = styled.div`
  background: ${(p) =>
    p.highlighted ? "rgba(64,224,208,0.06)" : "rgba(255,255,255,0.04)"};
  border: 1.5px solid
    ${(p) => (p.highlighted ? theme.colors.primary : theme.colors.border)};
  border-radius: 16px;
  padding: 36px 28px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(64, 224, 208, 0.1);
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.primary};
  color: #0c0a1d;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 16px;
  border-radius: 20px;
  white-space: nowrap;
`;

const PlanName = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
  color: ${theme.colors.text.primary};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const PriceAmount = styled.span`
  font-size: 2.8rem;
  font-weight: 800;
  color: ${(p) =>
    p.highlighted ? theme.colors.primary : theme.colors.text.primary};
  line-height: 1;
`;

const PriceSuffix = styled.span`
  font-size: 0.85rem;
  color: ${theme.colors.text.tertiary};
`;

const BillingNote = styled.p`
  font-size: 0.8rem;
  color: ${theme.colors.primary};
`;

const PlanDescription = styled.p`
  font-size: 0.9rem;
  color: ${theme.colors.text.tertiary};
  margin: 0;
  line-height: 1.5;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.border};
  margin: 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
`;

const PlanButton = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: auto;

  ${(p) =>
    p.variant === "filled"
      ? `
    background: ${theme.colors.primary};
    color: #0c0a1d;
    border: none;
    &:hover { background: #5EEEE0; }
  `
      : `
    background: transparent;
    color: ${theme.colors.text.primary};
    border: 1.5px solid ${theme.colors.border};
    &:hover { border-color: ${theme.colors.primary}; color: ${theme.colors.primary}; }
  `}
`;

// ─── Comparison Table ─────────────────────────────────────────────────────────
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
`;

const THead = styled.thead``;

const TBody = styled.tbody``;

const HeaderRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.border};
`;

const FeatureHeaderCell = styled.th`
  text-align: left;
  padding: 14px 16px;
  font-weight: 600;
  color: ${theme.colors.text.tertiary};
  font-size: 0.82rem;
  width: 35%;
`;

const PlanHeaderCell = styled.th`
  text-align: center;
  padding: 14px 16px;
  font-weight: 700;
  color: ${(p) =>
    p.highlighted ? theme.colors.primary : theme.colors.text.primary};
  font-size: 1rem;
  width: 21.6%;
`;

const CategoryRow = styled.tr`
  background: ${(p) =>
    p.isAi ? "rgba(64,224,208,0.08)" : "rgba(255,255,255,0.03)"};
`;

const CategoryCell = styled.td`
  padding: 12px 16px;
  font-weight: 700;
  font-size: 0.85rem;
  color: ${(p) =>
    p.isAi ? theme.colors.primary : theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryFullRow = styled.tr`
  background: ${(p) =>
    p.isAi ? "rgba(64,224,208,0.08)" : "rgba(255,255,255,0.03)"};
`;

const CategoryFullCell = styled.td`
  padding: 12px 16px;
  font-weight: 700;
  font-size: 0.85rem;
  color: ${(p) =>
    p.isAi ? theme.colors.primary : theme.colors.text.secondary};
`;

const AiCategoryLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FeatureRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const FeatureNameCell = styled.td`
  padding: 12px 16px;
  color: ${theme.colors.text.secondary};
`;

const FeatureValueCell = styled.td`
  padding: 12px 16px;
  text-align: center;
  color: ${theme.colors.text.secondary};
`;

const CheckMark = styled(CheckIcon)`
  color: ${theme.colors.primary} !important;
  font-size: 1.1rem !important;
`;

const CrossMark = styled(CloseIcon)`
  color: rgba(255, 255, 255, 0.2) !important;
  font-size: 1.1rem !important;
`;

const CtaRow = styled.tr`
  border-top: 1px solid ${theme.colors.border};
`;

const CtaCell = styled.td`
  padding: 20px 16px;
  text-align: center;
`;

const TableCtaButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  ${(p) =>
    p.variant === "filled"
      ? `
    background: ${theme.colors.primary};
    color: #0c0a1d;
    border: none;
    &:hover { background: #5EEEE0; }
  `
      : `
    background: transparent;
    color: ${theme.colors.text.primary};
    border: 1.5px solid ${theme.colors.border};
    &:hover { border-color: ${theme.colors.primary}; color: ${theme.colors.primary}; }
  `}
`;

// ─── FAQ ──────────────────────────────────────────────────────────────────────
const FaqSection = styled.section`
  padding: 80px 60px;
  border-top: 1px solid ${theme.colors.border};

  @media (max-width: 768px) {
    padding: 60px 24px;
  }
`;

const FaqInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const FaqLeft = styled.div``;

const FaqTitle = styled.h2`
  font-size: clamp(28px, 3.5vw, 40px);
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0 0 16px;
  line-height: 1.2;
`;

const FaqSubtitle = styled.p`
  font-size: 0.95rem;
  color: ${theme.colors.text.tertiary};
  margin: 0 0 32px;
  line-height: 1.6;
`;

const FaqCta = styled.button`
  background: ${theme.colors.primary};
  color: #0c0a1d;
  border: none;
  padding: 12px 28px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #5eeee0;
  }
`;

const FaqRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FaqItem = styled.div`
  border-bottom: 1px solid ${theme.colors.border};
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  background: transparent;
  border: none;
  color: ${theme.colors.text.primary};
  font-size: 0.98rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  gap: 16px;
  line-height: 1.4;
`;

const FaqAnswer = styled.div`
  font-size: 0.9rem;
  color: ${theme.colors.text.tertiary};
  line-height: 1.7;
  padding-bottom: 20px;
  max-height: ${(p) => (p.open ? "300px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

// ─── Component ────────────────────────────────────────────────────────────────
const PricingPage = () => {
  const navigate = useNavigate();
  const [pricingData, setPricingData] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetch("/pricing.json")
      .then((res) => res.json())
      .then((data) => setPricingData(data))
      .catch((err) => console.error("Failed to load pricing data:", err));
  }, []);

  const getDisplayPrice = (plan) => {
    if (plan.priceMonthly === null) return plan.priceLabel;
    if (plan.priceMonthly === 0) return plan.priceLabel;
    const price = isAnnual ? plan.priceAnnual : plan.priceMonthly;
    return `$${price}`;
  };

  const getSuffix = (plan) => {
    if (plan.priceMonthly === null || plan.priceMonthly === 0) return null;
    return plan.priceSuffix;
  };

  const renderValue = (val) => {
    if (typeof val === "boolean") {
      return val ? <CheckMark /> : <CrossMark />;
    }
    return <span style={{ color: theme.colors.text.secondary }}>{val}</span>;
  };

  const handlePlanAction = (plan) => {
    if (plan.id === "enterprise") {
      navigate("/letconnect");
    } else {
      navigate("/welcome");
    }
  };

  if (!pricingData) {
    return (
      <PageWrapper>
        <div
          style={{
            textAlign: "center",
            padding: "120px 0",
            color: theme.colors.text.tertiary,
          }}
        >
          Loading...
        </div>
      </PageWrapper>
    );
  }

  const { plans, comparisonTable, faq } = pricingData;

  return (
    <PageWrapper>
      {/* ── Section 1: Pricing Cards ── */}
      <Section>
        <SectionLabel>Pricing</SectionLabel>
        <SectionTitle>Simple, Transparent Pricing</SectionTitle>
        <SectionSubtitle>
          Choose the plan that fits your team. No hidden fees.
        </SectionSubtitle>

        <ToggleWrapper>
          <ToggleLabel active={!isAnnual}>Monthly</ToggleLabel>
          <ToggleTrack annual={isAnnual} onClick={() => setIsAnnual((p) => !p)}>
            <ToggleThumb annual={isAnnual} />
          </ToggleTrack>
          <ToggleLabel active={isAnnual}>Annual</ToggleLabel>
          <SaveBadge>Save 20%</SaveBadge>
        </ToggleWrapper>

        <CardsGrid>
          {plans.map((plan) => (
            <PlanCard key={plan.id} highlighted={plan.isHighlighted}>
              {plan.badge && <Badge>{plan.badge}</Badge>}

              <PlanName>{plan.tier}</PlanName>

              <div>
                <PriceRow>
                  <PriceAmount highlighted={plan.isHighlighted}>
                    {getDisplayPrice(plan)}
                  </PriceAmount>
                  {getSuffix(plan) && (
                    <PriceSuffix>{getSuffix(plan)}</PriceSuffix>
                  )}
                </PriceRow>
                {isAnnual && plan.billingNote && (
                  <BillingNote>{plan.billingNote}</BillingNote>
                )}
              </div>

              <PlanDescription>{plan.description}</PlanDescription>

              <Divider />

              <FeatureList>
                {plan.features.map((f, i) => (
                  <FeatureItem key={i}>
                    {f.isAi ? (
                      <AutoAwesomeIcon
                        sx={{ fontSize: "16px", color: theme.colors.primary }}
                      />
                    ) : (
                      <CheckIcon
                        sx={{ fontSize: "16px", color: theme.colors.primary }}
                      />
                    )}
                    {f.label}
                  </FeatureItem>
                ))}
              </FeatureList>

              <PlanButton
                variant={plan.buttonVariant}
                onClick={() => handlePlanAction(plan)}
              >
                {plan.buttonText}
              </PlanButton>
            </PlanCard>
          ))}
        </CardsGrid>
      </Section>

      {/* ── Section 2: Compare Features ── */}
      <section
        style={{
          borderTop: `1px solid ${theme.colors.border}`,
          padding: "80px 60px",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <SectionLabel>Details</SectionLabel>
          <SectionTitle>
            Compare{" "}
            <span style={{ color: theme.colors.primary }}>Features</span>
          </SectionTitle>
          <SectionSubtitle>
            Detailed breakdown of what's included in each plan.
          </SectionSubtitle>

          <TableWrapper>
            <Table>
              <THead>
                <HeaderRow>
                  <FeatureHeaderCell>Features</FeatureHeaderCell>
                  {plans.map((p) => (
                    <PlanHeaderCell key={p.id} highlighted={p.isHighlighted}>
                      {p.tier}
                    </PlanHeaderCell>
                  ))}
                </HeaderRow>
              </THead>

              <TBody>
                {comparisonTable.categories.map((cat) => (
                  <React.Fragment key={cat.name}>
                    {/* Category header row */}
                    <CategoryFullRow isAi={cat.isAi}>
                      <CategoryFullCell colSpan={4} isAi={cat.isAi}>
                        {cat.isAi ? (
                          <AiCategoryLabel>
                            <AutoAwesomeIcon
                              sx={{
                                fontSize: "15px",
                                color: theme.colors.primary,
                              }}
                            />
                            {cat.name}
                          </AiCategoryLabel>
                        ) : (
                          cat.name
                        )}
                      </CategoryFullCell>
                    </CategoryFullRow>

                    {/* Feature rows */}
                    {cat.features.map((feat) => (
                      <FeatureRow key={feat.name}>
                        <FeatureNameCell>{feat.name}</FeatureNameCell>
                        <FeatureValueCell>
                          {renderValue(feat.free)}
                        </FeatureValueCell>
                        <FeatureValueCell>
                          {renderValue(feat.standard)}
                        </FeatureValueCell>
                        <FeatureValueCell>
                          {renderValue(feat.enterprise)}
                        </FeatureValueCell>
                      </FeatureRow>
                    ))}
                  </React.Fragment>
                ))}

                {/* CTA row */}
                <CtaRow>
                  <FeatureNameCell />
                  {plans.map((plan) => (
                    <CtaCell key={plan.id}>
                      <TableCtaButton
                        variant={plan.buttonVariant}
                        onClick={() => handlePlanAction(plan)}
                      >
                        {plan.buttonText}
                      </TableCtaButton>
                    </CtaCell>
                  ))}
                </CtaRow>
              </TBody>
            </Table>
          </TableWrapper>
        </div>
      </section>

      {/* ── Section 3: FAQ ── */}
      <FaqSection>
        <FaqInner>
          <FaqLeft>
            <FaqTitle>Frequently Asked Questions</FaqTitle>
            <FaqSubtitle>
              Detailed breakdown of what's included in each plan.
            </FaqSubtitle>
            <FaqCta onClick={() => navigate("/welcome")}>
              Get Started Free
            </FaqCta>
          </FaqLeft>

          <FaqRight>
            {faq.map((item, i) => (
              <FaqItem key={i}>
                <FaqQuestion
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{item.question}</span>
                  {openFaq === i ? (
                    <RemoveIcon
                      sx={{
                        fontSize: "18px",
                        flexShrink: 0,
                        color: theme.colors.primary,
                      }}
                    />
                  ) : (
                    <AddIcon
                      sx={{
                        fontSize: "18px",
                        flexShrink: 0,
                        color: theme.colors.text.tertiary,
                      }}
                    />
                  )}
                </FaqQuestion>
                <FaqAnswer open={openFaq === i}>{item.answer}</FaqAnswer>
              </FaqItem>
            ))}
          </FaqRight>
        </FaqInner>
      </FaqSection>
    </PageWrapper>
  );
};

export default PricingPage;
